import React, { useState, useEffect, useRef } from 'react';
import './geographicalSetup.css';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import axios from "axios";

// Search relevance: 0 = starts with query, 1 = a word starts with query,
// 2 = contains query anywhere, -1 = no match
const getMatchRank = (text, query) => {
  const t = (text || '').toLowerCase();
  const q = (query || '').trim().toLowerCase();
  if (!q) return 0;
  if (t.startsWith(q)) return 0;
  if (t.split(/[\s\-(),.]+/).some(word => word.startsWith(q))) return 1;
  if (t.includes(q)) return 2;
  return -1;
};

// Best (lowest) rank across several fields, e.g. port name + country
const getBestMatchRank = (texts, query) => {
  const ranks = texts.map(t => getMatchRank(t, query)).filter(r => r >= 0);
  return ranks.length ? Math.min(...ranks) : -1;
};

// Searchable dropdown (used for Country in Add Port form)
const SearchableSelect = ({ id, options, value, onChange, placeholder = 'Select', emptyText = 'No results found' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Best matches first ("in" -> India before Argentina), A–Z within same rank
  const filtered = options
    .map((opt, index) => ({ opt, index, rank: getMatchRank(opt, query) }))
    .filter(item => item.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(item => item.opt);

  const openList = () => {
    setQuery('');
    const selectedIndex = options.indexOf(value);
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const closeList = () => {
    setOpen(false);
    setQuery('');
  };

  const selectOption = (opt) => {
    onChange(opt);
    closeList();
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeList();
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Focus search box when opened
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  // Keep highlighted item visible
  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.children[highlight];
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) selectOption(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeList();
    } else if (e.key === 'Tab') {
      closeList();
    }
  };

  const onTriggerKeyDown = (e) => {
    if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  return (
    <div className={`search-select ${open ? 'open' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        id={id}
        className="search-select-trigger"
        onClick={() => (open ? closeList() : openList())}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value ? 'search-select-value' : 'search-select-placeholder'}>
          {value || placeholder}
        </span>
        <svg className="search-select-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="search-select-dropdown">
          <input
            ref={searchRef}
            type="text"
            className="search-select-search"
            placeholder="Search country..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
            onKeyDown={onSearchKeyDown}
          />
          <ul className="search-select-list" role="listbox" ref={listRef}>
            {filtered.length === 0 ? (
              <li className="search-select-empty">{emptyText}</li>
            ) : (
              filtered.map((opt, index) => (
                <li
                  key={opt}
                  role="option"
                  aria-selected={opt === value}
                  className={`search-select-option ${index === highlight ? 'highlighted' : ''} ${opt === value ? 'selected' : ''}`}
                  onMouseEnter={() => setHighlight(index)}
                  onMouseDown={(e) => { e.preventDefault(); selectOption(opt); }}
                >
                  <span>{opt}</span>
                  {opt === value && <span className="search-select-check">✓</span>}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const GeographicalSetup = () => {
  // Active tab is kept in the URL (?tab=port) so it survives page refresh
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'port' ? 'port' : 'country';
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('tab') === activeTab) return;
    url.searchParams.set('tab', activeTab);
    // replaceState: no new history entry, router state preserved
    window.history.replaceState(window.history.state, '', url.toString());
  }, [activeTab]);
  const [countries, setCountries] = useState([]);
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState({ countries: false, ports: false });
  const [error, setError] = useState({ countries: null, ports: null });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [editingItem, setEditingItem] = useState({ type: null, id: null });
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [portSearchTerm, setPortSearchTerm] = useState('');

  // Country table sorting: key = column, dir = 'asc' | 'desc' | null
  const [countrySort, setCountrySort] = useState({ key: null, dir: null });

  // Port table sorting: key = column, dir = 'asc' | 'desc' | null
  const [portSort, setPortSort] = useState({ key: null, dir: null });

  // Exact country filter for Port List (set from Country table "Ports" count)
  const [portCountryFilter, setPortCountryFilter] = useState('');

  // Table "docking": page scrolls until the table reaches the top bar,
  // then scrolling continues inside the table
  const ADMIN_TOPBAR_HEIGHT = 70; // height of the fixed "Admin Dashboard" bar
  const tableContainerRef = useRef(null);
  const [tableDocked, setTableDocked] = useState(false);

  useEffect(() => {
    const checkDocked = () => {
      const el = tableContainerRef.current;
      if (!el) return;
      const reachedTop = el.getBoundingClientRect().top <= ADMIN_TOPBAR_HEIGHT + 16;
      const atPageBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      setTableDocked(reachedTop || atPageBottom);
    };

    checkDocked();
    window.addEventListener('scroll', checkDocked, { passive: true });
    window.addEventListener('resize', checkDocked);
    return () => {
      window.removeEventListener('scroll', checkDocked);
      window.removeEventListener('resize', checkDocked);
    };
  }, [activeTab, loading.countries, loading.ports]);

  // Add state for country inactive warning popup
  const [showInactiveCountryPopup, setShowInactiveCountryPopup] = useState(false);
  const [inactiveCountryMessage, setInactiveCountryMessage] = useState('');

  // Filter functions
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const filteredPorts = ports.filter(port =>
    (!portCountryFilter || port.country === portCountryFilter) &&
    (
      port.name.toLowerCase().includes(portSearchTerm.toLowerCase()) ||
      port.country.toLowerCase().includes(portSearchTerm.toLowerCase())
    )
  );

  // Port count per country (matched by country name)
  const portCountByCountry = ports.reduce((acc, port) => {
    acc[port.country] = (acc[port.country] || 0) + 1;
    return acc;
  }, {});

  // Sort value for each sortable country column
  const getCountrySortValue = (country, key) => {
    switch (key) {
      case 'id': return country.id;
      case 'name': return country.name?.toLowerCase() || '';
      case 'isoCode': return country.isoCode?.toLowerCase() || '';
      case 'countryPhoneCode': {
        const digits = (country.countryPhoneCode || '').replace(/[^0-9]/g, '');
        return digits ? Number(digits) : '';
      }
      case 'ports': return portCountByCountry[country.name] || 0;
      case 'status': return country.status;
      default: return '';
    }
  };

  const sortedCountries = (() => {
    if (!countrySort.key || !countrySort.dir) {
      // No column sort: while searching, show best matches first
      if (!countrySearchTerm.trim()) return filteredCountries;
      return filteredCountries
        .map((country, index) => ({ country, index, rank: getMatchRank(country.name, countrySearchTerm) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(item => item.country);
    }
    const factor = countrySort.dir === 'asc' ? 1 : -1;
    return [...filteredCountries].sort((a, b) => {
      const va = getCountrySortValue(a, countrySort.key);
      const vb = getCountrySortValue(b, countrySort.key);
      // Empty values always go to the bottom
      if (va === '' && vb !== '') return 1;
      if (vb === '' && va !== '') return -1;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  })();

  // Click cycle: asc -> desc -> default
  const handleCountrySort = (key) => {
    setCountrySort(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  };

  const renderSortIcon = (key) => {
    if (countrySort.key !== key) return <span className="sort-icon inactive">⇅</span>;
    return <span className="sort-icon">{countrySort.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  // ----- Port table sorting -----
  const getPortSortValue = (port, key) => {
    switch (key) {
      case 'id': return port.id;
      case 'country': return port.country?.toLowerCase() || '';
      case 'name': return port.name?.toLowerCase() || '';
      case 'status': return port.status;
      default: return '';
    }
  };

  const sortedPorts = (() => {
    if (!portSort.key || !portSort.dir) {
      // No column sort: while searching, show best matches first (port name or country)
      if (!portSearchTerm.trim()) return filteredPorts;
      return filteredPorts
        .map((port, index) => ({ port, index, rank: getBestMatchRank([port.name, port.country], portSearchTerm) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(item => item.port);
    }
    const factor = portSort.dir === 'asc' ? 1 : -1;
    return [...filteredPorts].sort((a, b) => {
      const va = getPortSortValue(a, portSort.key);
      const vb = getPortSortValue(b, portSort.key);
      // Empty values always go to the bottom
      if (va === '' && vb !== '') return 1;
      if (vb === '' && va !== '') return -1;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  })();

  // Click cycle: asc -> desc -> default
  const handlePortSort = (key) => {
    setPortSort(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  };

  const renderPortSortIcon = (key) => {
    if (portSort.key !== key) return <span className="sort-icon inactive">⇅</span>;
    return <span className="sort-icon">{portSort.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  // Open Port tab filtered by the selected country
  const handleViewCountryPorts = (countryName) => {
    setPortCountryFilter(countryName);
    setPortSearchTerm('');
    setActiveTab('port');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Counter functions
  const getCountryStats = () => {
    const active = filteredCountries.filter(c => c.status === 'Active').length;
    const inactive = filteredCountries.filter(c => c.status === 'Inactive').length;
    return { active, inactive, total: filteredCountries.length };
  };

  const getPortStats = () => {
    const active = filteredPorts.filter(p => p.status === 'Active').length;
    const inactive = filteredPorts.filter(p => p.status === 'Inactive').length;
    return { active, inactive, total: filteredPorts.length };
  };

  const countryStats = getCountryStats();
  const portStats = getPortStats();

  // Helper function to check if a country is active
  const isCountryActive = (countryName) => {
    const country = countries.find(c => c.name === countryName);
    return country?.status === 'Active';
  };

  const PinkSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#4caf50',
      '&:hover': {
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#4caf50',
    },
    '& .MuiSwitch-switchBase': {
      color: '#e91e63',
      '&:hover': {
        backgroundColor: '#f8bbd0',
      },
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#e91e63',
    },
  }));

  const label = { inputProps: { 'aria-label': 'Toggle port status' } };

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPortData, setEditPortData] = useState({ portId: null, newPortName: '' });
  const [editLoading, setEditLoading] = useState(false);
  // Original port name when the modal opened (for heading + change detection)
  const [editPortOriginalName, setEditPortOriginalName] = useState('');

  const isPortEditChanged =
    editPortData.newPortName.trim() !== editPortOriginalName.trim();

  const closeEditPortModal = () => {
    if (editLoading) return;
    setShowEditModal(false);
  };

  // Esc closes the Edit Port modal
  useEffect(() => {
    if (!showEditModal) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !editLoading) {
        setShowEditModal(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showEditModal, editLoading]);

  // Country edit modal states
  const [showEditCountryModal, setShowEditCountryModal] = useState(false);
  const [editCountryData, setEditCountryData] = useState({ countryId: null, countryName: '', isoCode: '', countryPhoneCode: '' });
  const [editCountryLoading, setEditCountryLoading] = useState(false);
  // Original values when the modal opened (for heading + change detection)
  const [editCountryOriginal, setEditCountryOriginal] = useState({ countryName: '', isoCode: '', countryPhoneCode: '' });

  const isCountryEditChanged =
    editCountryData.countryName.trim() !== editCountryOriginal.countryName.trim() ||
    editCountryData.isoCode.trim() !== editCountryOriginal.isoCode.trim() ||
    editCountryData.countryPhoneCode.trim() !== editCountryOriginal.countryPhoneCode.trim();

  const closeEditCountryModal = () => {
    if (editCountryLoading) return;
    setShowEditCountryModal(false);
  };

  // Esc closes the Edit Country modal
  useEffect(() => {
    if (!showEditCountryModal) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !editCountryLoading) {
        setShowEditCountryModal(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showEditCountryModal, editCountryLoading]);

  // Country delete state (id of the country currently being deleted)
  const [deletingCountryId, setDeletingCountryId] = useState(null);

  // Port delete state (id of the port currently being deleted)
  const [deletingPortId, setDeletingPortId] = useState(null);

  // Form states
  const [countryForm, setCountryForm] = useState({ name: '', isoCode: '', countryPhoneCode: '', status: 'Active' });
  const [portForm, setPortForm] = useState({ country: '', name: '', status: 'Active' });

  // Fetch countries data
  const fetchCountries = async () => {
    setLoading(prev => ({ ...prev, countries: true }));
    setError(prev => ({ ...prev, countries: null }));
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/all-countries', {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch countries');
      }
      const data = await response.json();
      setCountries(data.map(country => ({
        id: country.countryId,
        name: country.countryName,
        isoCode: country.isoCode || '',
        countryPhoneCode: country.countryPhoneCode || '',
       status: country.isActive ? 'Active' : 'Inactive'  // Use actual status from API
      })));
    } catch (err) {
      setError(prev => ({ ...prev, countries: err.message }));
      console.error('Error fetching countries:', err);
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch ports data
  const fetchPorts = async () => {
    setLoading(prev => ({ ...prev, ports: true }));
    setError(prev => ({ ...prev, ports: null }));
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/ports', {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch ports');
      }
      const result = await response.json();
      // Backend now returns { ports: [...], activePortCount, inactivePortCount, totalPortCount }
      const data = Array.isArray(result) ? result : Array.isArray(result?.ports) ? result.ports : [];
      if (data.length && data[0].isActive === undefined) {
        console.warn('Ports API response has no "isActive" field - status will show as Active:', data[0]);
      }
      setPorts(data.map(port => ({
        id: port.portId,
        country: port.countryName,
        name: port.portName,
        // Use real status from API (fallback to Active if field is missing)
        status: port.isActive === false ? 'Inactive' : 'Active'
      })));
    } catch (err) {
      setError(prev => ({ ...prev, ports: err.message }));
      console.error('Error fetching ports:', err);
    } finally {
      setLoading(prev => ({ ...prev, ports: false }));
    }
  };

  useEffect(() => {
    fetchPorts();
  }, []);

  const handleCountrySubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(prev => ({ ...prev, countries: true }));

      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/add-new-countries', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          countryName: countryForm.name.trim(),
          isoCode: countryForm.isoCode.trim(),
          countryPhoneCode: countryForm.countryPhoneCode.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add country');
      }

      const data = await response.json();
      console.log('Country added successfully:', data);

      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      await fetchCountries();
      setCountryForm({ name: '', isoCode: '', countryPhoneCode: '', status: 'Active' });

    } catch (err) {
      setError(prev => ({ ...prev, countries: err.message }));
      console.error('Error adding country:', err);
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };

  const handlePortSubmit = async (e) => {
    e.preventDefault();

    // Custom dropdown has no native "required", so validate here
    if (!portForm.country) {
      alert('Please select a country');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, ports: true }));

      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/add-new-ports', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          portName: portForm.name,
          countryName: portForm.country
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add port');
      }

      const data = await response.json();
      console.log('Port added successfully:', data);

      // Add API has no status field: if "Inactive" was selected, set it right after creating
      if (portForm.status === 'Inactive') {
        if (data?.portId) {
          await axios.put(
            'https://car.amar.co.jp/api/authorization/shipping/update-port-status',
            {},
            {
              params: { portId: data.portId, isActive: false },
              headers: { Accept: 'application/json' }
            }
          );
        } else {
          console.warn('Add port response has no portId - could not set Inactive status:', data);
        }
      }

      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      await fetchPorts();
      setPortForm({ country: '', name: '', status: 'Active' });

    } catch (err) {
      setError(prev => ({ ...prev, ports: err.message }));
      console.error('Error adding port:', err);
    } finally {
      setLoading(prev => ({ ...prev, ports: false }));
    }
  };

  const toggleStatus = (type, id) => {
    switch (type) {
      case 'country':
        setCountries(countries.map(c =>
          c.id === id ? { ...c, status: c.status === 'Active' ? 'Inactive' : 'Active' } : c
        ));
        break;
      case 'port':
        setPorts(ports.map(p =>
          p.id === id ? { ...p, status: p.status === 'Active' ? 'Inactive' : 'Active' } : p
        ));
        break;
      default:
        break;
    }
  };

  // Updated handle edit port with country check
  const handleEditPort = (port) => {
    // Check if the port's country is active
    if (!isCountryActive(port.country)) {
      setInactiveCountryMessage(`Cannot edit port "${port.name}" because country "${port.country}" is inactive. Please activate the country first.`);
      setShowInactiveCountryPopup(true);
      return;
    }

    setEditPortData({
      portId: port.id,
      newPortName: port.name
    });
    setEditPortOriginalName(port.name);
    setShowEditModal(true);
  };

  // Handle edit action for countries (unchanged)
  const handleEditCountry = (country) => {
    const current = {
      countryName: country.name,
      isoCode: country.isoCode || '',
      countryPhoneCode: country.countryPhoneCode || ''
    };
    setEditCountryData({ countryId: country.id, ...current });
    setEditCountryOriginal(current);
    setShowEditCountryModal(true);
  };

  const handlePortStatusToggle = async (port) => {
    // Check if the port's country is active
    if (!isCountryActive(port.country)) {
      setInactiveCountryMessage(
        `Cannot change status of port "${port.name}" because country "${port.country}" is inactive. Please activate the country first.`
      );
      setShowInactiveCountryPopup(true);
      return;
    }

    try {
      const isActive = port.status !== "Active";

      console.log("📤 Updating port status:", { portId: port.id, isActive });

      const response = await axios.put(
        "https://car.amar.co.jp/api/authorization/shipping/update-port-status",
        {},
        {
          params: { portId: port.id, isActive },
          headers: {
            Accept: "application/json",
          },
        }
      );

      console.log("✅ API Response:", response.data);

      // Update frontend state
      setPorts((prev) =>
        prev.map((p) =>
          p.id === port.id
            ? { ...p, status: isActive ? "Active" : "Inactive" }
            : p
        )
      );

      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (error) {
      console.error("❌ API error:", error);

      let errorMessage = "Unknown error";
      if (error.response?.status === 404) {
        errorMessage = "Please make first Active Country";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage = error.message;
      }

      alert(`Failed to update port status: ${errorMessage}`);
    }
  };

  // Handle country name update
  const handleUpdateCountryName = async () => {
    if (!editCountryData.countryName.trim()) {
      alert('Please enter a valid country name');
      return;
    }
    if (!editCountryData.isoCode.trim()) {
      alert('Please enter a valid ISO code');
      return;
    }
    if (!editCountryData.countryPhoneCode.trim()) {
      alert('Please enter a valid country phone code');
      return;
    }

    try {
      setEditCountryLoading(true);

      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/update-country', {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          countryId: editCountryData.countryId,
          countryName: editCountryData.countryName.trim(),
          isoCode: editCountryData.isoCode.trim(),
          countryPhoneCode: editCountryData.countryPhoneCode.trim()
        })
      });

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.log('Response is not JSON, but request was successful:', responseText);
        data = { message: responseText };
      }

      console.log('Country updated successfully:', data);

      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      setShowEditCountryModal(false);
      await fetchCountries();

    } catch (err) {
      console.error('Error updating country:', err);
      alert('Failed to update country name: ' + err.message);
    } finally {
      setEditCountryLoading(false);
    }
  };

  // Handle port name update
  const handleUpdatePortName = async () => {
    if (!editPortData.newPortName.trim()) {
      alert('Please enter a valid port name');
      return;
    }

    try {
      setEditLoading(true);

      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/update-port-name', {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          portId: editPortData.portId,
          newPortName: editPortData.newPortName
        })
      });

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.log('Response is not JSON, but request was successful:', responseText);
        data = { message: responseText };
      }

      console.log('Port updated successfully:', data);

      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      setShowEditModal(false);
      await fetchPorts();

    } catch (err) {
      console.error('Error updating port:', err);
      alert('Failed to update port name: ' + err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle edit action
  const handleEdit = (type, item) => {
    setEditingItem({ type, id: item.id });

    if (type === 'country') {
      handleEditCountry(item);
    } else if (type === 'port') {
      handleEditPort(item);
    }
  };

  // Handle country delete (API) - also deletes its related seaports on backend
  const handleDeleteCountry = async (countryId) => {
    const country = countries.find(c => c.id === countryId);
    const countryName = country?.name || 'this country';

    if (!window.confirm(`Are you sure you want to delete ${countryName}? All seaports of this country will also be deleted.`)) {
      return;
    }

    try {
      setDeletingCountryId(countryId);

      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/delete-country/${countryId}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json'
        }
      });

      // Backend returns plain text: "Country and related seaports deleted successfully"
      const responseText = await response.text();
      console.log('Raw response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      // Refresh both lists since related ports are deleted too
      await Promise.all([fetchCountries(), fetchPorts()]);

    } catch (err) {
      console.error('Error deleting country:', err);
      alert('Failed to delete country: ' + err.message);
    } finally {
      setDeletingCountryId(null);
    }
  };

  // Handle port delete (API)
  const handleDeletePort = async (portId) => {
    const port = ports.find(p => p.id === portId);
    const portLabel = port ? `${port.name} (${port.country})` : 'this port';

    if (!window.confirm(`Are you sure you want to delete ${portLabel}?`)) {
      return;
    }

    try {
      setDeletingPortId(portId);

      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/delete-port/${portId}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json'
        }
      });

      // Backend returns plain text: "Seaport with ID <id> deleted successfully."
      const responseText = await response.text();
      console.log('Raw response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      // Refresh ports (Port Count in Country table updates from this too)
      await fetchPorts();

    } catch (err) {
      console.error('Error deleting port:', err);
      alert('Failed to delete port: ' + err.message);
    } finally {
      setDeletingPortId(null);
    }
  };

  // Handle delete action
  const handleDelete = (type, id) => {
    if (type === 'country') {
      handleDeleteCountry(id);
      return;
    }
    if (type === 'port') {
      handleDeletePort(id);
    }
  };

  return (
    <div className="geographical-setup">
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-geographical-setup">
          <div className="popup-content-geographical-setup">
            <p>Operation completed successfully!</p>
          </div>
        </div>
      )}

      {/* Inactive Country Warning Popup */}
      {showInactiveCountryPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚠️ Country Inactive</h3>
            <div className="modal-form">
              <p style={{ color: '#e91e63', fontWeight: 'bold' }}>
                {inactiveCountryMessage}
              </p>
            </div>
            <div className="modal-buttons">
              <button
                className="save-btn"
                onClick={() => {
                  setShowInactiveCountryPopup(false);
                  setInactiveCountryMessage('');
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Country Modal */}
      {showEditCountryModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="edit-modal-title">Edit Country – {editCountryOriginal.countryName}</h3>
            <div
              className="modal-form"
              onKeyDown={(e) => {
                // Enter inside any field submits (only if something changed)
                if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                  e.preventDefault();
                  if (isCountryEditChanged && !editCountryLoading) {
                    handleUpdateCountryName();
                  }
                }
              }}
            >
              <label htmlFor="edit-country-name">Country Name*</label>
              <input
                type="text"
                id="edit-country-name"
                autoFocus
                value={editCountryData.countryName}
                onChange={(e) => setEditCountryData(prev => ({
                  ...prev,
                  countryName: e.target.value
                }))}
                placeholder="e.g. India"
              />
              <label htmlFor="edit-country-iso-code" style={{ marginTop: '1rem' }}>ISO Code*</label>
              <input
                type="text"
                id="edit-country-iso-code"
                maxLength={3}
                value={editCountryData.isoCode}
                onChange={(e) => setEditCountryData(prev => ({
                  ...prev,
                  isoCode: e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase()
                }))}
                placeholder="e.g. IN"
              />
              <label htmlFor="edit-country-phone-code" style={{ marginTop: '1rem' }}>Country Phone Code*</label>
              <input
                type="text"
                id="edit-country-phone-code"
                maxLength={6}
                value={editCountryData.countryPhoneCode}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, '');
                  setEditCountryData(prev => ({
                    ...prev,
                    countryPhoneCode: digits ? `+${digits}` : ''
                  }));
                }}
                placeholder="e.g. +91"
              />
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeEditCountryModal}
                disabled={editCountryLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-btn"
                onClick={handleUpdateCountryName}
                disabled={editCountryLoading || !isCountryEditChanged}
                title={!isCountryEditChanged ? 'No changes to update' : ''}
              >
                {editCountryLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Port Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="edit-modal-title">Edit Port – {editPortOriginalName}</h3>
            <div
              className="modal-form"
              onKeyDown={(e) => {
                // Enter inside the field submits (only if something changed)
                if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                  e.preventDefault();
                  if (isPortEditChanged && !editLoading) {
                    handleUpdatePortName();
                  }
                }
              }}
            >
              <label htmlFor="edit-port-name">Port Name*</label>
              <input
                type="text"
                id="edit-port-name"
                autoFocus
                value={editPortData.newPortName}
                onChange={(e) => setEditPortData(prev => ({
                  ...prev,
                  newPortName: e.target.value
                }))}
                placeholder="e.g. Mombasa"
              />
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeEditPortModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-btn"
                onClick={handleUpdatePortName}
                disabled={editLoading || !isPortEditChanged}
                title={!isPortEditChanged ? 'No changes to update' : ''}
              >
                {editLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="setup-header">
        <h2>Geographical Setup</h2>
        {/* <p className="setup-subtitle">Manage countries and seaports used for shipping</p> */}
      </div>

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'country'}
          className={`tab-btn ${activeTab === 'country' ? 'active' : ''}`}
          onClick={() => setActiveTab('country')}
        >
          <svg className="tab-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          Countries
          <span className="tab-count">{countries.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'port'}
          className={`tab-btn ${activeTab === 'port' ? 'active' : ''}`}
          onClick={() => setActiveTab('port')}
        >
          <svg className="tab-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="5" r="3" />
            <path d="M12 22V8" />
            <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
          </svg>
          Ports
          <span className="tab-count">{ports.length}</span>
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'country' && (
          <div className="country-section">
            <div className="form-container">
              <h3>Add New Country</h3>
              <form onSubmit={handleCountrySubmit} className="geo-form">
                <div className="form-group">
                  <label htmlFor="country-name">Country Name*</label>
                  <input
                    type="text"
                    id="country-name"
                    placeholder="e.g. India"
                    value={countryForm.name}
                    onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="country-iso-code">ISO Code*</label>
                  <input
                    type="text"
                    id="country-iso-code"
                    placeholder="e.g. IN"
                    maxLength={3}
                    value={countryForm.isoCode}
                    onChange={(e) => setCountryForm({ ...countryForm, isoCode: e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase() })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="country-phone-code">Country Phone Code*</label>
                  <input
                    type="text"
                    id="country-phone-code"
                    placeholder="e.g. +91"
                    maxLength={6}
                    value={countryForm.countryPhoneCode}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/[^0-9]/g, '');
                      setCountryForm({ ...countryForm, countryPhoneCode: digits ? `+${digits}` : '' });
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div className="status-toggle">
                    <label className={`status-toggle-option is-active ${countryForm.status === 'Active' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="country-status"
                        checked={countryForm.status === 'Active'}
                        onChange={() => setCountryForm({ ...countryForm, status: 'Active' })}
                      />
                      <span className="status-dot"></span> Active
                    </label>
                    <label className={`status-toggle-option is-inactive ${countryForm.status === 'Inactive' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="country-status"
                        checked={countryForm.status === 'Inactive'}
                        onChange={() => setCountryForm({ ...countryForm, status: 'Inactive' })}
                      />
                      <span className="status-dot"></span> Inactive
                    </label>
                  </div>
                </div>
                <div className="form-group country-submit-group">
                  <label aria-hidden="true">&nbsp;</label>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading.countries}
                  >
                    {loading.countries ? 'Adding...' : 'Add Country'}
                  </button>
                </div>
              </form>
            </div>

            <div className="list-container">
              <h3>Country List</h3>

              <div className="list-toolbar">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>

                <div className="status-counters">
                  <div className="counter-item active-counter">
                    <span className="counter-label">Active:</span>
                    <span className="counter-value">{countryStats.active}</span>
                  </div>
                  <div className="counter-item inactive-counter">
                    <span className="counter-label">Inactive:</span>
                    <span className="counter-value">{countryStats.inactive}</span>
                  </div>
                  <div className="counter-item total-counter">
                    <span className="counter-label">Total:</span>
                    <span className="counter-value">{countryStats.total}</span>
                  </div>
                </div>
              </div>

              {loading.countries ? (
                <div>Loading countries...</div>
              ) : error.countries ? (
                <div className="error-message">Error: {error.countries}</div>
              ) : (
                <div
                  ref={tableContainerRef}
                  className={`geo-table-container ${tableDocked ? 'is-docked' : ''}`}
                >
                  <table className="geo-table">
                    <thead>
                      <tr>
                        <th className="sortable-th" onClick={() => handleCountrySort('id')}>ID {renderSortIcon('id')}</th>
                        <th className="sortable-th" onClick={() => handleCountrySort('name')}>Country {renderSortIcon('name')}</th>
                        <th className="sortable-th" onClick={() => handleCountrySort('isoCode')}>ISO Code {renderSortIcon('isoCode')}</th>
                        <th className="sortable-th" onClick={() => handleCountrySort('countryPhoneCode')}>Phone Code {renderSortIcon('countryPhoneCode')}</th>
                        <th className="sortable-th col-center" onClick={() => handleCountrySort('ports')}>Port Count {renderSortIcon('ports')}</th>
                        <th className="sortable-th col-center" onClick={() => handleCountrySort('status')}>Status {renderSortIcon('status')}</th>
                        <th className="col-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCountries.map(country => (
                        <tr key={country.id}>
                          <td>{country.id}</td>
                          <td>{country.name}</td>
                          <td>{country.isoCode || '—'}</td>
                          <td>{country.countryPhoneCode || '—'}</td>
                          <td className="col-center">
                            {portCountByCountry[country.name] ? (
                              <button
                                type="button"
                                className="port-count-btn"
                                title={`View ports of ${country.name}`}
                                onClick={() => handleViewCountryPorts(country.name)}
                              >
                                {portCountByCountry[country.name]}
                              </button>
                            ) : (
                              <span className="port-count-zero">0</span>
                            )}
                          </td>
                          <td className="col-center">
                            <span className={`status-badge ${country.status.toLowerCase()}`}>
                              {country.status}
                            </span>
                          </td>
                          <td className="col-center">
                            <div className="action-buttons-geo">
                              <PinkSwitch
                                {...label}
                                checked={country.status === "Active"}
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  console.log("🌍 Current country:", country);

                                  try {
                                    const payload = {
                                      countryId: country.id,
                                      isActive: country.status !== "Active",
                                    };

                                    console.log("📤 Sending payload:", payload);

                                    const response = await axios.put(
                                      "https://car.amar.co.jp/api/authorization/shipping/update-country-status",
                                      payload,
                                      {
                                        headers: {
                                          "Content-Type": "application/json",
                                          Accept: "application/json",
                                        },
                                      }
                                    );

                                    console.log("✅ API success:", response.data);

                                    setCountries(prev =>
                                      prev.map(c =>
                                        c.id === country.id
                                          ? { ...c, status: payload.isActive ? "Active" : "Inactive" }
                                          : c
                                      )
                                    );

                                    setShowSuccessPopup(true);
                                    setTimeout(() => {
                                      setShowSuccessPopup(false);
                                    }, 3000);

                                  } catch (error) {
                                    console.error("❌ API error:", error);
                                    alert("Failed to update country status. Please try again.");
                                  }
                                }}
                              />
                              <button
                                className="action-btn all-car-edit-btn"
                                title="Edit"
                                onClick={() => handleEdit('country', country)}
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white" style={{pointerEvents:"none"}}>
                                  <path fill="white" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                </svg>
                              </button>
                              <button
                                className="action-btn all-car-delete-btn"
                                title="Delete"
                                disabled={deletingCountryId === country.id}
                                onClick={() => handleDelete('country', country.id)}
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white" style={{pointerEvents:"none"}}>
                                  <path fill="white" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredCountries.length === 0 && countrySearchTerm && (
                    <div className="no-results">No countries found matching "{countrySearchTerm}"</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'port' && (
          <div className="port-section">
            <div className="form-container">
              <h3>Add New Port</h3>
              <form onSubmit={handlePortSubmit} className="geo-form">
                <div className="form-group">
                  <label htmlFor="port-country">Country*</label>
                  <SearchableSelect
                    id="port-country"
                    placeholder="Select Country"
                    emptyText="No countries found"
                    value={portForm.country}
                    onChange={(countryName) => setPortForm({ ...portForm, country: countryName })}
                    options={countries
                      .filter(c => c.status === 'Active')
                      .map(c => c.name)
                      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="port-name">Port Name*</label>
                  <input
                    type="text"
                    id="port-name"
                    placeholder="e.g. Mombasa"
                    value={portForm.name}
                    onChange={(e) => setPortForm({ ...portForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div className="status-toggle">
                    <label className={`status-toggle-option is-active ${portForm.status === 'Active' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="port-status"
                        checked={portForm.status === 'Active'}
                        onChange={() => setPortForm({ ...portForm, status: 'Active' })}
                      />
                      <span className="status-dot"></span> Active
                    </label>
                    <label className={`status-toggle-option is-inactive ${portForm.status === 'Inactive' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="port-status"
                        checked={portForm.status === 'Inactive'}
                        onChange={() => setPortForm({ ...portForm, status: 'Inactive' })}
                      />
                      <span className="status-dot"></span> Inactive
                    </label>
                  </div>
                </div>
                <div className="form-group port-submit-group">
                  <label aria-hidden="true">&nbsp;</label>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading.ports}
                  >
                    {loading.ports ? 'Adding...' : 'Add Port'}
                  </button>
                </div>
              </form>
            </div>

            <div className="list-container">
              <h3>Port List</h3>

              <div className="list-toolbar">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search ports..."
                    value={portSearchTerm}
                    onChange={(e) => setPortSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>

                {portCountryFilter && (
                  <div className="port-country-filter-chip">
                    <span>Country: <strong>{portCountryFilter}</strong></span>
                    <button
                      type="button"
                      title="Clear country filter"
                      onClick={() => setPortCountryFilter('')}
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className="status-counters">
                  <div className="counter-item active-counter">
                    <span className="counter-label">Active:</span>
                    <span className="counter-value">{portStats.active}</span>
                  </div>
                  <div className="counter-item inactive-counter">
                    <span className="counter-label">Inactive:</span>
                    <span className="counter-value">{portStats.inactive}</span>
                  </div>
                  <div className="counter-item total-counter">
                    <span className="counter-label">Total:</span>
                    <span className="counter-value">{portStats.total}</span>
                  </div>
                </div>
              </div>

              {loading.ports ? (
                <div>Loading ports...</div>
              ) : error.ports ? (
                <div className="error-message">Error: {error.ports}</div>
              ) : (
                <div
                  ref={tableContainerRef}
                  className={`geo-table-container ${tableDocked ? 'is-docked' : ''}`}
                >
                  <table className="geo-table">
                    <thead>
                      <tr>
                        <th className="sortable-th" onClick={() => handlePortSort('id')}>ID {renderPortSortIcon('id')}</th>
                        <th className="sortable-th" onClick={() => handlePortSort('country')}>Country {renderPortSortIcon('country')}</th>
                        <th className="sortable-th" onClick={() => handlePortSort('name')}>Port {renderPortSortIcon('name')}</th>
                        <th className="sortable-th col-center" onClick={() => handlePortSort('status')}>Status {renderPortSortIcon('status')}</th>
                        <th className="col-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPorts.map(port => (
                        <tr key={port.id}>
                          <td>{port.id}</td>
                          <td>
                            {port.country}
                            {!isCountryActive(port.country) && (
                              <span
                                style={{
                                  color: '#e91e63',
                                  fontSize: '12px',
                                  marginLeft: '5px',
                                  fontWeight: 'bold'
                                }}
                              >
                                (Inactive)
                              </span>
                            )}
                          </td>
                          <td>{port.name}</td>
                          <td className="col-center">
                            <span className={`status-badge ${port.status.toLowerCase()}`}>
                              {port.status}
                            </span>
                          </td>
                          <td className="col-center">
                            <div className="action-buttons-geo">
                              <PinkSwitch
                                {...label}
                                checked={port.status === 'Active'}
                                disabled={!isCountryActive(port.country)}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handlePortStatusToggle(port);
                                }}
                                style={{
                                  opacity: !isCountryActive(port.country) ? 0.5 : 1,
                                  cursor: !isCountryActive(port.country) ? 'not-allowed' : 'pointer'
                                }}
                              />
                              <button
                                className="action-btn all-car-edit-btn"
                                title={!isCountryActive(port.country) ? "Cannot edit - Country is inactive" : "Edit"}
                                onClick={() => handleEdit('port', port)}
                                disabled={!isCountryActive(port.country)}
                                style={{
                                  opacity: !isCountryActive(port.country) ? 0.5 : 1,
                                  cursor: !isCountryActive(port.country) ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white" style={{pointerEvents:"none"}}>
                                  <path fill="white" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                </svg>
                              </button>
                              <button
                                className="action-btn all-car-delete-btn"
                                title="Delete"
                                disabled={deletingPortId === port.id}
                                onClick={() => handleDelete('port', port.id)}
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white" style={{pointerEvents:"none"}}>
                                  <path fill="white" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredPorts.length === 0 && (portSearchTerm || portCountryFilter) && (
                    <div className="no-results">
                      No ports found{portCountryFilter ? ` for "${portCountryFilter}"` : ''}{portSearchTerm ? ` matching "${portSearchTerm}"` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeographicalSetup;